import type { Request, Response, NextFunction, RequestHandler } from "express";
import { eq } from "drizzle-orm";
import { fromNodeHeaders } from "better-auth/node";
import { auth } from "../auth/index";
import { db } from "../db/index";
import { users } from "../db/schema/identity";
import { getEffectivePermissions } from "./effective-permissions";
import { isModuleEnabled } from "./module-gate";
import { SCOPE, type ScopeId, type ScopeName } from "./scopes";
import type { EffectivePermissions } from "./effective-permissions";

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace Express {
    interface Request {
      auth?: { userId: number; companyId: number };
      perms?: EffectivePermissions;
      scope?: ScopeId;
    }
  }
}

async function resolveCompany(userId: number): Promise<number | null> {
  const row = await db
    .select({ companyId: users.companyId })
    .from(users)
    .where(eq(users.id, userId))
    .limit(1);
  return row[0]?.companyId ?? null;
}

/**
 * Attach the authenticated user. Tries the Better Auth session; in local dev accepts
 * `x-dev-user-id` (+ optional `x-dev-company-id`) as a fallback for testing the RBAC layer
 * before the full Better Auth ↔ users mapping is finalized in the Auth step. 401 if neither.
 */
export const requireAuth: RequestHandler = async (req, res, next) => {
  try {
    let userId: number | null = null;
    let fromSession = false;
    // Prefer a real Better Auth session (so logging in as any role takes effect); fall back to the
    // dev header only when there's no session (local testing before/without login). Never in prod.
    const session = await auth.api.getSession({ headers: fromNodeHeaders(req.headers) });
    if (session?.user?.id != null) {
      userId = Number(session.user.id);
      fromSession = true;
    } else if (process.env.NODE_ENV !== "production") {
      const dev = req.header("x-dev-user-id");
      if (dev) userId = Number(dev);
    }
    if (!userId || Number.isNaN(userId)) return res.status(401).json({ error: "Unauthenticated" });

    // Company comes from the user's row when authenticated by session; the dev header is honored
    // only on the dev fallback path.
    const devCompany = req.header("x-dev-company-id");
    const companyId =
      !fromSession && devCompany && process.env.NODE_ENV !== "production"
        ? Number(devCompany)
        : await resolveCompany(userId);
    if (!companyId) return res.status(401).json({ error: "No company for user" });

    req.auth = { userId, companyId };
    next();
  } catch (e) {
    next(e);
  }
};

/**
 * Gate a route on a scoped permission. 403 if the caller's effective scope is NONE; otherwise
 * attaches `req.perms` + `req.scope` so the handler applies `scopeCondition(req.scope, …)` to its query.
 */
export function requirePermission(permission: string): RequestHandler {
  return async (req: Request, res: Response, next: NextFunction) => {
    if (!req.auth) return res.status(401).json({ error: "Unauthenticated" });
    const perms = await getEffectivePermissions(req.auth.userId);
    const scope = perms[permission] ?? SCOPE.NONE;
    if (scope === SCOPE.NONE) return res.status(403).json({ error: "Forbidden", permission });
    req.perms = perms;
    req.scope = scope;
    next();
  };
}

/** Gate a route on module enablement (reskin-docs/02 §5). */
export function requireModule(
  moduleName: string,
  type: "admin" | "employee" | "client" = "admin",
): RequestHandler {
  return async (req: Request, res: Response, next: NextFunction) => {
    if (!req.auth) return res.status(401).json({ error: "Unauthenticated" });
    if (!(await isModuleEnabled(req.auth.companyId, moduleName, type)))
      return res.status(403).json({ error: "Module disabled", module: moduleName });
    next();
  };
}

export type { ScopeName };
